import {BLEND_MODES, Container, Point, Renderer, Sprite} from 'pixi.js';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {ResourceLoader} from '@gongxigames/framework/core/services/resourceLoader/ResourceLoader';
import postHtmlParser, {Node, NodeTag} from 'posthtml-parser';
import {CursorContext} from '@gongxigames/framework/core/fragments/display/richText/CursorContext';
import {LineItemType} from '@gongxigames/framework/core/fragments/display/richText/Line';
import {Tags} from '@gongxigames/framework/core/fragments/display/richText/Tags';
import {RichTextTextStyle} from '@gongxigames/framework/core/fragments/display/richText/TextStyle';
import {LanguageService} from '@gongxigames/framework/core/services/LanguageService';
import {AUTOMATION_META, AutomationMeta} from '@gongxigames/framework/core/fragments/Autotest';

export * from '@gongxigames/framework/core/fragments/display/richText/TextStyle';

const REG_EX_NEW_LINES = /(\r\n|\r|\n)/ug;

export type DisplayConfig = {
    /**
     * Text max width, 0 - means no max width. Default is 0.
     */
    maxWidth?: number;

    /**
     * Text max height, 0 - means no max height. Default is 0.
     */
    maxHeight?: number;

    /**
     * Text render resolution. Default is 1.
     */
    resolution?: number;

    position?: Point;
    scale?: Point;
    rotation?: number;
    blendMode?: BLEND_MODES;
};

export type RichTextOptions = DisplayConfig & {
    /**
     * string id from [[LanguageService]] strings
     * or
     * any unique id if customText property is defined
     */
    id: string;
    /**
     * if customText is defined, we will use it as is
     * instead of using LanguageService
     */
    customText?: string;
    /**
     *  style preset name from [[RichText.styles]]
     *  or
     *  style config, described in scene *.tsx
     */
    style: string | Partial<RichTextTextStyle>;
    /**
     * Name for the root container
     */
    name?: string;
    /**
     * Values Object for rendering in text template
     */
    values?: Record<string, string | number>;
    lang?: string;
};

export type RichTextConstructorOptions = RichTextOptions & {
    /**
     * [[LanguageService]] instance required to get current localization texts
     */
    languageService: LanguageService;
    /**
     * An [[ResourceLoader]] instance. Required for inserting images.
     */
    resourceLoader: ResourceLoader;
};

export class RichText extends Sprite {
    /* develblock:start */
    /**
     * Only can be used in development mode, for WebPack hot reload.
     */
    static _hotReloadStrings: RichText[] = [];
    /* develblock:end */

    /**
     * Available TextStyle presets.
     */
    static readonly styles: Record<string, RichTextTextStyle> = {};

    /**
     * Available TextStyle presets.
     */
    static readonly displayConfigs: Record<string, DisplayConfig> = {};

    protected _languageService: LanguageService;
    protected _resourceLoader: ResourceLoader;
    protected _style!: RichTextTextStyle;
    protected _styleName!: string;
    protected _lang: string;
    protected _id!: string;
    protected _customText?: string;
    protected _maxWidth = 0;
    protected _maxHeight = 0;
    protected _resolution = 1;
    protected _values: Record<string, string | number> = {};
    protected _context?: CursorContext;
    protected _container: Container = new Container();
    protected _toDestroy?: LineItemType[];

    static setStyle(id: string, style: RichTextTextStyle | Partial<RichTextTextStyle>, {locales}: {locales?: string[];} = {}): void {
        if (!style.clone) {
            style = new RichTextTextStyle(style);
        }

        if (locales?.length) {
            locales.forEach((l) => {
                RichText.styles[`${id}_${l}`] = style as RichTextTextStyle;
            });
        } else {
            RichText.styles[id] = style as RichTextTextStyle;
        }
    }

    static getStyle(styleName: string, lang?: string): RichTextTextStyle {
        return RichText.styles[`${styleName}_${lang}`] ?? RichText.styles[styleName];
    }

    static setDisplayConfig(id: string, displayConfig: DisplayConfig): void {
        RichText.displayConfigs[id] = displayConfig;
    }

    static applyDisplayConfig(id: string, options: Partial<RichTextOptions>): Partial<RichTextOptions> {
        const displayConfig = RichText.displayConfigs[id] || {};

        return {...options, ...displayConfig} as RichTextConstructorOptions;
    }

    /* develblock:start */
    /**
     * Only can be used in development mode, for WebPack hot reload.
     */
    static hotReload(): void {
        RichText._hotReloadStrings.forEach(text => text.updateText());
    }
    /* develblock:end */

    constructor(options: RichTextConstructorOptions) {
        super();

        this.name = options.name || `RichText:${options.id}`;
        this._languageService = options.languageService;
        this._resourceLoader = options.resourceLoader;
        this._lang = options.lang ?? '';
        this.blendMode = options.blendMode || BLEND_MODES.NORMAL;

        this.addChild(this._container);

        this.updateText(options);

        /* develblock:start */
        // Only can be used in development mode, for WebPack hot reload.
        RichText._hotReloadStrings.push(this);
        /* develblock:end */
    }

    public getText(): string {
        return this._customText ?? this._languageService.get(this._id);
    }

    public getTextToDisplay(): string {
        return this.setValues(this.getText());
    }

    public getTextId(): string {
        return this._id;
    }

    public getMaxWidth(): number {
        return this._maxWidth;
    }

    public getStyles(): RichTextTextStyle {
        return this._style;
    }

    /**
     * Recreate the text completely, use properties to update text properties.
     * Or set values in placeholders
     */
    public updateText(options: Partial<RichTextOptions> = {}): this {
        if (this._destroyed) {
            return this;
        }

        this._id = options?.id ?? this._id;
        this._customText = options?.customText ?? this._customText;
        this._styleName = typeof options?.style === 'string' ? options.style : this._styleName;

        options = RichText.applyDisplayConfig(this._id, options);
        this._maxWidth = options?.maxWidth ?? this._maxWidth;
        this._maxHeight = options?.maxHeight ?? this._maxHeight;
        this._resolution = options?.resolution ?? this._resolution;
        options.position?.copyTo(this.position);
        options.scale?.copyTo(this.scale);
        this.rotation = options.rotation ?? this.rotation;

        if (options.style && typeof options.style !== 'string') {
            this._style = new RichTextTextStyle(options.style);
        } else if (this._styleName) {
            this._style = RichText.getStyle(this._styleName, this._lang);
        }

        if (!this._style) {
            throw new Error(`RichText: style: '${this._styleName}' is missing`);
        }

        if (this._style.position) {
            this.position.copyFrom(this._style.position);
        }

        if (this._style.scale) {
            this.scale.copyFrom(this._style.scale);
        }

        if (options.values) {
            this._values = {...this._values, ...options.values};
        }

        const text = this.getTextToDisplay();
        const [lines, toDestroy] = this.renderLines(text);

        // ram and video memory cleanup
        this._toDestroy?.forEach(item => (item.destroy as any)(true));
        this._toDestroy = toDestroy;

        this._container.removeChildren();
        this._container.addChild(lines);

        return this.updateSize();
    }

    public render(renderer: Renderer): void {
        if (this._context) {
            this._context.applyMasks(renderer);
            this._context = undefined;
        }

        super.render(renderer);
    }

    public get width(): number {
        return this._container.width;
    }

    public get height(): number {
        return this._container.height;
    }

    protected updateSize(): this {
        this._container.scale.set(1.0);

        const {width, height} = this._container;

        if (this._maxWidth > 0 && width > this._maxWidth) {
            const scale = this._maxWidth / width;

            this._container.scale.set(scale);
        }

        if (this._maxHeight > 0 && height > this._maxHeight) {
            const scale = this._maxHeight / height;

            this._container.scale.set(Math.min(this._container.scale.x, scale));
        }

        return this;
    }

    protected setValues(str: string): string {
        for (const [key, val] of Object.entries(this._values)) {
            str = str.replace(new RegExp(`{{${key}}}`, 'g'), String(val));
        }

        return str;
    }

    protected renderLines(str: string): [Container, LineItemType[]] {
        str = str.replace(REG_EX_NEW_LINES, '<br/>');

        const nodeTree = postHtmlParser(str, {xmlMode: true});

        this._context = new CursorContext(this._style, nodeTree, RichText.styles, this._resolution, this._resourceLoader);
        this.applyTags(this._context, nodeTree);

        return this._context.render();
    }

    protected applyTags(context: CursorContext, nodeTree: Node[]): void {
        this.traverseAST(nodeTree, (node, depth) => {
            context.setDepth(depth);

            if (typeof node !== 'string') {
                node.tag = (node.tag as string).toLowerCase();
            }

            const tag = Tags.find(tag => tag.test(node));

            if (!tag) {
                const tagName = (node as NodeTag).tag;

                throw new Error(`RichText: Incorrect tag <${tagName}>`);
            }

            try {
                tag.run(context, node, this.blendMode);
            } catch (error) {
                consoleLogger.error(error);
            }

        }, 1);
    }

    protected traverseAST(tree: Node[], fn: (elem: Node, depth: number) => void, depth = 0): void {
        tree.forEach(node => {
            fn(node, depth);

            if ((node as NodeTag).content) {
                this.traverseAST((node as NodeTag).content as Node[], fn, depth + 1);
            }
        });
    }

    [AUTOMATION_META](): AutomationMeta {
        return {
            type: 'text',
            text: this.getTextToDisplay(),
            textTemplate: this.getText()
        };
    }
}
